import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { isSessionActive } from '@/stores/session'

declare module 'vue-router' {
  interface RouteMeta {
    /** Skips the auth guard entirely. */
    public?: boolean
    /** Redirects an already-authenticated fisherman away (the login screen). */
    guestOnly?: boolean
    /** i18n key for the top bar title, inside the authenticated shell. */
    title?: string
    /** Shows a back chevron in the top bar instead of the app name. */
    back?: boolean
  }
}

// Every route is lazy — this app targets a slow island link, so nothing
// beyond the login screen and the app shell belongs in the initial chunk.
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => (isSessionActive.value ? '/home' : '/login'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, guestOnly: true },
  },
  {
    path: '/verify/:code',
    name: 'verify-receipt',
    component: () => import('@/views/VerifyReceiptView.vue'),
    meta: { public: true },
  },
  {
    path: '/sports',
    name: 'sports-requirements',
    component: () => import('@/views/SportsRequirementsView.vue'),
    meta: { public: true },
  },
  {
    path: '/sports/:id',
    name: 'sports-requirement-detail',
    component: () => import('@/views/SportsRequirementDetailView.vue'),
    meta: { public: true },
    props: true,
  },
  {
    path: '/sports/register',
    name: 'sports-register',
    component: () => import('@/views/SportsRegisterView.vue'),
    meta: { public: true },
  },
  {
    path: '/collection-points/:id',
    name: 'collection-point',
    component: () => import('@/views/CollectionPointView.vue'),
    meta: { public: true },
    props: true,
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { title: 'app.name' },
  },
  {
    path: '/vessels',
    name: 'vessels',
    component: () => import('@/views/VesselsView.vue'),
    meta: { title: 'vessels.title' },
  },
  {
    path: '/queue',
    name: 'queue',
    component: () => import('@/views/QueueView.vue'),
    meta: { title: 'queue.title' },
  },
  {
    path: '/bills',
    name: 'bills',
    component: () => import('@/views/BillsView.vue'),
    meta: { title: 'bills.title' },
  },
  {
    path: '/bills/:id',
    name: 'bill-detail',
    component: () => import('@/views/BillDetailView.vue'),
    props: true,
    meta: { title: 'billDetail.title', back: true },
  },
  {
    path: '/payments',
    name: 'payments',
    component: () => import('@/views/PaymentsView.vue'),
    meta: { title: 'payments.title' },
  },
  {
    path: '/wallet',
    name: 'wallet',
    component: () => import('@/views/WalletView.vue'),
    meta: { title: 'wallet.title' },
  },
  {
    path: '/announcements',
    name: 'announcements',
    component: () => import('@/views/AnnouncementsView.vue'),
    meta: { title: 'announcements.title' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { title: 'profile.title' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach((to) => {
  const authed = isSessionActive.value
  if (to.meta.guestOnly && authed) return { name: 'home' }
  if (!to.meta.public && !authed) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  return true
})

export default router
